import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
  ArrowLeft,
  Bell,
  Check,
  Clock,
  Loader2,
  Palette,
  Shield,
} from "lucide-react";
import { defaultAgendaSettings, type AgendaSettings } from "@bookdental/schemas";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useFeature } from "@/lib/use-feature";
import { useSetPageHeader } from "@/lib/page-header";
import { useIsMobile } from "@/lib/use-media-query";
import { ScheduleSection } from "@/components/settings/ScheduleSection";
import { VisualSection } from "@/components/settings/VisualSection";
import { RemindersSection } from "@/components/settings/RemindersSection";
import { BehaviorSection } from "@/components/settings/BehaviorSection";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { useOcupaRodape } from "@/components/ajuda/RodapeOcupado";
import { CONFIG_AGENDA_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";

// "procedures" saiu daqui na unificação de Serviço × Procedimento — o catálogo (com duração e cor)
// agora é editado só em /servicos.
type SectionKey = "schedule" | "visual" | "reminders" | "behavior";

const SECTIONS: {
  key: SectionKey;
  label: string;
  description: string;
  icon: React.ReactNode;
  accent: string;
}[] = [
  {
    key: "schedule",
    label: "Horários",
    description: "Slot da grade, duração padrão e fins de semana",
    icon: <Clock className="h-4 w-4" />,
    accent: "#2457ff",
  },
  {
    key: "visual",
    label: "Visual",
    description: "Cards, grid e linha do horário atual",
    icon: <Palette className="h-4 w-4" />,
    accent: "#ec4899",
  },
  {
    key: "reminders",
    label: "Lembretes",
    description: "WhatsApp automático e template",
    icon: <Bell className="h-4 w-4" />,
    accent: "#10b981",
  },
  {
    key: "behavior",
    label: "Comportamento",
    description: "Conflitos, walk-in e confirmações",
    icon: <Shield className="h-4 w-4" />,
    accent: "#f59e0b",
  },
];

/** `embutido`: dentro de uma seção de outra página (Configurações, Minha conta) — sem título
 *  próprio, sem "voltar" e com as subseções em abas, já que a de fora tem a barra lateral. */
export function SettingsAgenda({ embutido = false }: { embutido?: boolean } = {}) {
  const ocupaRodape = useOcupaRodape<HTMLDivElement>();
  const compacto = useIsMobile() || embutido;
  // Embutida, quem registra o tour é a página de fora (registrar aqui seria sobrescrito).
  useTourDaPagina(embutido ? [] : CONFIG_AGENDA_TOUR_STEPS);
  useSetPageHeader({
    title: "Configurações · Agenda",
    subtitle: "Ajuste como a agenda se comporta na clínica",
  }, [], !embutido);

  const hasWhatsappReminder = useFeature("agenda.whatsappReminder");
  // "Lembretes" (WhatsApp) some da lista pra quem não tem a feature, em vez de aparecer travado.
  const visibleSections = SECTIONS.filter((s) => s.key !== "reminders" || hasWhatsappReminder);
  const [section, setSection] = useState<SectionKey>("schedule");
  const utils = trpc.useUtils();
  const query = trpc.settings.getAgenda.useQuery();
  const [values, setValues] = useState<AgendaSettings>(defaultAgendaSettings());

  const update = trpc.settings.updateAgenda.useMutation({
    onSuccess: () => {
      toast.success("Configurações salvas");
      utils.settings.getAgenda.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao salvar"),
  });

  useEffect(() => {
    if (query.data) setValues(query.data);
  }, [query.data]);

  const onChange = (partial: Partial<AgendaSettings>) =>
    setValues((prev) => ({ ...prev, ...partial }));

  const handleSave = () => update.mutate(values);
  const handleReset = () => {
    setValues(defaultAgendaSettings());
    toast.info("Valores padrão carregados (não salvos ainda)");
  };

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  const activeSection = visibleSections.find((s) => s.key === section) ?? visibleSections[0];

  return (
    <div className={`${embutido ? "" : "mx-auto max-w-6xl "}w-full space-y-4 pb-24`}>
      {!embutido && (
        <Link
          to="/configuracoes"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Configurações
        </Link>
      )}

      {compacto ? (
        <MobileTabs sections={visibleSections} section={section} onChange={setSection} />
      ) : null}

      <div className="flex gap-6">
        {!compacto && (
          <DesktopSidebar sections={visibleSections} section={section} onChange={setSection} />
        )}

        <div className="min-w-0 flex-1">
          <div className="rounded-xl border border-zinc-200 bg-white shadow-sm">
            {/* Section header */}
            <div className="flex items-center gap-3 border-b border-zinc-100 px-6 py-4">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: `${activeSection.accent}1a`,
                  color: activeSection.accent,
                }}
              >
                {activeSection.icon}
              </div>
              <div>
                <h2 className="text-sm font-semibold text-zinc-900">
                  {activeSection.label}
                </h2>
                <p className="text-xs text-zinc-500">{activeSection.description}</p>
              </div>
            </div>

            {/* Section body */}
            <div className="px-4 py-5 sm:px-6">
              {section === "schedule" && (
                <ScheduleSection values={values} onChange={onChange} />
              )}
              {section === "visual" && (
                <VisualSection values={values} onChange={onChange} />
              )}
              {section === "reminders" && hasWhatsappReminder && (
                <RemindersSection values={values} onChange={onChange} />
              )}
              {section === "behavior" && (
                <BehaviorSection values={values} onChange={onChange} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer sticky */}
      <div
        ref={ocupaRodape}
        // No celular fica em cima da barra de abas, que já cobre a área do gesto.
        className="fixed bottom-0 left-0 right-0 z-30 border-t border-zinc-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-sm max-md:bottom-[calc(3.75rem+env(safe-area-inset-bottom))] max-md:pb-0"
      >
        <div className="mx-auto flex w-full max-w-6xl items-center justify-end gap-2 px-4 py-3 sm:px-6 md:px-8">
          <button
            type="button"
            onClick={handleReset}
            disabled={update.isPending}
            className="inline-flex h-10 items-center rounded-md border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-700 transition-colors hover:border-zinc-300 hover:bg-zinc-50 disabled:opacity-60 sm:h-9"
          >
            Restaurar padrões
          </button>
          <button
            type="button"
            data-tour="config-agenda-salvar"
            onClick={handleSave}
            disabled={update.isPending}
            className="inline-flex h-10 items-center gap-1.5 rounded-md bg-brand-600 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60 sm:h-9 sm:px-4 sm:font-medium"
          >
            {update.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {update.isPending ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DesktopSidebar({
  sections,
  section,
  onChange,
}: {
  sections: typeof SECTIONS;
  section: SectionKey;
  onChange: (s: SectionKey) => void;
}) {
  return (
    <aside data-tour="config-agenda-secoes" className="w-60 shrink-0">
      <nav className="sticky top-20 space-y-1">
        {sections.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                active
                  ? "bg-zinc-50 text-zinc-900"
                  : "text-zinc-700 hover:bg-zinc-50"
              }`}
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-transform group-hover:scale-105"
                style={{
                  backgroundColor: `${s.accent}1a`,
                  color: s.accent,
                }}
              >
                {s.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{s.label}</p>
                <p className="truncate text-[11px] text-zinc-500 group-hover:text-zinc-600">
                  {s.description}
                </p>
              </div>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

function MobileTabs({
  sections,
  section,
  onChange,
}: {
  sections: typeof SECTIONS;
  section: SectionKey;
  onChange: (s: SectionKey) => void;
}) {
  return (
    <div data-tour="config-agenda-secoes" className="-mx-4 overflow-x-auto border-b border-zinc-200 bg-white sm:-mx-6">
      <div className="flex gap-1 px-4 sm:px-6">
        {sections.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={`inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium transition-colors ${
                active
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-zinc-600 hover:text-zinc-900"
              }`}
            >
              {s.icon}
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
